/**
 * Plan comparison — opened from the plan panel.
 *
 * A price list on its own makes an admin do arithmetic against their own
 * volume, so every tier is also priced *at this organisation's projected run
 * rate*. That turns three abstract fees into one comparable number per tier,
 * which is the only form in which the question "should we move?" can be
 * answered.
 *
 * Note it deliberately does not push an upgrade. On the published rates the
 * per-page overage is usually cheaper than the next tier's fee step, so the
 * reason to move up is the capabilities, and those are listed plainly for the
 * admin to weigh.
 *
 * Rendered through a portal into document.body: `fixed` positioning is only
 * fixed to the viewport when no ancestor applies a transform, and this page's
 * own entrance animations do exactly that — an untouched ancestor would make
 * the modal scroll with the page instead of staying centred (see the
 * identical bug fixed on DetailDrawer).
 */
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Check, Gem, Layers, Sparkles, X } from 'lucide-react'
import { useBodyScrollLock, usePrefersReducedMotion } from './BillingPrimitives'
import { formatInt, formatMoneyAs } from '../../utils/billing'

const PLAN_ICONS = { starter: Sparkles, scale: Layers, enterprise: Gem }

export default function PlanComparison({ plan, open, onClose, displayCurrency }) {
  const panelRef = useRef(null)
  const reduced = usePrefersReducedMotion()
  useBodyScrollLock(open)

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    const previous = document.activeElement
    panelRef.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      previous?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null

  const currency = plan?.currency || { code: 'GBP', symbol: '£' }
  const catalogue = plan?.catalogue || []
  const projectedPages = plan?.usage?.projected_pages || 0
  // Only an explicit false: an older backend sends no flag. Early in a cycle a
  // run rate is one upload extrapolated, so nothing is called "Lowest" on it.
  const reliable = plan?.usage?.projection_reliable !== false
  // The allowance bars share one scale: the biggest allowance on offer.
  const maxIncluded = Math.max(1, ...catalogue.map(t => Number(t.included_pages) || 0))
  const fmt = (value, opts) => formatMoneyAs(value, currency, displayCurrency, opts)

  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <button
        type="button"
        aria-label="Close plan comparison"
        onClick={onClose}
        className={`fixed inset-0 bg-gray-900/50 dark:bg-black/70 backdrop-blur-[2px] ${reduced ? '' : 'animate-fade-in'}`}
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-comparison-title"
        className={`relative w-full max-w-5xl max-h-[calc(100dvh-2rem)] flex flex-col
                    bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800
                    shadow-2xl focus:outline-none ${reduced ? '' : 'animate-modal-in'}`}
      >
        <div className="flex items-start gap-4 p-5 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.09em] text-gray-500 dark:text-gray-400">
              Pricing plans
            </p>
            <h2 id="plan-comparison-title" className="text-lg font-bold text-gray-900 dark:text-gray-100 mt-1">
              Choose your document volume &amp; control level
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
              Three tiers aligned to Azure Marketplace billing. Each base fee includes a monthly page
              allowance; you only pay overage beyond it. {reliable
                ? <>Projected costs below use this organisation&apos;s current run rate of {formatInt(projectedPages)} pages a month.</>
                : <>It is too early in this billing month to project a run rate, so the projected costs below are only indicative.</>}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                       hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0">
        <div className="p-5 pt-7 grid grid-cols-1 md:grid-cols-3 gap-4">
          {catalogue.map((tier, i) => {
            const Icon = PLAN_ICONS[tier.id] || Layers
            const current = tier.is_current
            // A plan that can't be bought yet is listed, but never priced as an
            // option: no "Lowest" badge, no call to action.
            const comingSoon = tier.available === false && !current
            const purchasable = catalogue.filter(t => t.available !== false)
            const cheapest = reliable && !comingSoon && purchasable.length > 1
              && purchasable.every(t => t.projected_cost >= tier.projected_cost)

            return (
              <div
                key={tier.id}
                className={`relative rounded-xl border p-5 flex flex-col transition-all duration-300
                  ${reduced ? '' : 'animate-card-in'}
                  ${current
                    ? 'border-navy-600 dark:border-navy-400 ring-1 ring-navy-600/30 dark:ring-navy-400/30 bg-navy-50/40 dark:bg-navy-900/10'
                    : comingSoon
                      ? 'border-dashed border-gray-200 dark:border-gray-800 opacity-70'
                      : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700 hover:-translate-y-0.5 hover:shadow-lg'}`}
                style={{ animationDelay: `${80 + i * 70}ms` }}
              >
                {(current || comingSoon || tier.popular) && (
                  <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase
                                    tracking-wider px-2.5 py-1 rounded-full whitespace-nowrap
                    ${current
                      ? 'bg-navy-600 dark:bg-navy-500 text-white'
                      : comingSoon
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200'
                        : 'bg-gray-900 dark:bg-gray-700 text-white'}`}>
                    {current ? 'Your plan' : comingSoon ? 'Coming soon' : 'Most popular'}
                  </span>
                )}

                <div className="flex items-center gap-2 mt-1">
                  <span className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-800 text-navy-700 dark:text-navy-300">
                    <Icon className="w-4 h-4" />
                  </span>
                  <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">{tier.name}</h3>
                </div>

                <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500 mt-4">
                  Target use-case
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 min-h-[2.5rem]">{tier.tagline}</p>

                <div className="mt-4 pt-4 border-t border-dashed border-gray-200 dark:border-gray-700 flex items-baseline gap-1.5">
                  <span className="text-3xl font-bold text-gray-900 dark:text-gray-100 tabular-nums">
                    {fmt(tier.monthly_fee, { pence: false })}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">/ month</span>
                </div>

                <div className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
                      {formatInt(tier.included_pages)} pages
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                      Included
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 mt-2 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-navy-600 dark:bg-navy-400 transition-[width] duration-700 ease-out"
                      style={{ width: `${((Number(tier.included_pages) || 0) / maxIncluded) * 100}%` }}
                    />
                  </div>
                  <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-2">
                    + {fmt(tier.overage_per_page)} / page overage
                  </p>
                </div>

                <div className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500">
                    At your run rate
                  </p>
                  <p className="text-sm font-bold text-gray-900 dark:text-gray-100 tabular-nums mt-0.5">
                    {fmt(tier.projected_cost)}
                    <span className="text-[11px] font-normal text-gray-500 dark:text-gray-400"> / month</span>
                    {cheapest && (
                      <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                        Lowest
                      </span>
                    )}
                  </p>
                  {tier.projected_overage_pages > 0 && (
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 tabular-nums">
                      includes {formatInt(tier.projected_overage_pages)} pages of overage
                    </p>
                  )}
                </div>

                <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-400 dark:text-gray-500 mt-4">
                  Key capabilities
                </p>
                <ul className="mt-2 space-y-1.5 flex-1">
                  {(tier.capabilities || []).map(cap => (
                    <li key={cap} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300">
                      <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
                      {cap}
                    </li>
                  ))}
                </ul>

                {current || comingSoon ? (
                  <div className="mt-5 text-center text-[13px] font-semibold text-gray-400 dark:text-gray-500
                                  border border-gray-200 dark:border-gray-800 rounded-lg py-2">
                    {current ? 'Current plan' : 'Coming soon'}
                  </div>
                ) : (
                  <Link
                    to="/contact-support"
                    onClick={onClose}
                    className="mt-5 inline-flex items-center justify-center gap-2 text-[13px] font-semibold
                               rounded-lg py-2 border border-gray-200 dark:border-gray-700
                               text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800
                               hover:border-gray-300 dark:hover:border-gray-600 transition-colors
                               focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
                  >
                    Talk to us about {tier.name}
                  </Link>
                )}
              </div>
            )
          })}
        </div>

        <p className="px-5 pb-5 text-[11px] text-gray-500 dark:text-gray-400">
          Plans marked coming soon can&apos;t be bought yet. To talk about your plan, contact us. Projected
          costs are estimates from usage recorded here; your Microsoft Marketplace invoice is the authority.
        </p>
        </div>
      </div>
    </div>,
    document.body,
  )
}
